<template>
  <v-card class="dashboard-widget" :class="[widgetClass, { 'dashboard-widget--dense': dense }]" elevation="2">
    <v-card-text>
      <div class="widget-header">
        <div class="widget-icon">
          <v-icon :color="iconColor" :large="!dense" :small="dense">{{ icon }}</v-icon>
        </div>
        <div class="widget-actions">
          <v-btn icon x-small @click="refreshWidget" v-if="refreshable">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-menu offset-y v-if="hasMenu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="(menuItem, index) in menuItems" :key="index" @click="handleMenuAction(menuItem.action)">
                <v-list-item-icon>
                  <v-icon small>{{ menuItem.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ menuItem.label }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div class="widget-content">
        <h2 class="widget-value" v-if="!loading">{{ formattedValue }}</h2>
        <v-skeleton-loader type="heading" v-else></v-skeleton-loader>

        <p class="widget-title">{{ title }}</p>

        <div class="widget-trend" v-if="trend && !loading">
          <v-icon :color="trendColor" small>{{ trendIcon }}</v-icon>
          <span :class="trendClass">{{ trend }}</span>
          <span class="trend-period">{{ trendPeriod }}</span>
        </div>
      </div>

      <div class="widget-footer" v-if="showFooter">
        <v-progress-linear
          :value="progressValue"
          :color="progressColor"
          height="4"
          rounded
          v-if="showProgress"
        ></v-progress-linear>

        <div class="widget-subtitle" v-if="subtitle">
          {{ subtitle }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'DashboardWidget',
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      default: 0
    },
    icon: {
      type: String,
      default: 'mdi-information'
    },
    color: {
      type: String,
      default: 'primary'
    },
    trend: {
      type: String,
      default: null
    },
    trendDirection: {
      type: String,
      default: 'up', // 'up', 'down', 'neutral'
      validator: value => ['up', 'down', 'neutral'].includes(value)
    },
    trendPeriod: {
      type: String,
      default: 'vs last month'
    },
    loading: {
      type: Boolean,
      default: false
    },
    refreshable: {
      type: Boolean,
      default: false
    },
    hasMenu: {
      type: Boolean,
      default: false
    },
    menuItems: {
      type: Array,
      default: () => []
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    progressValue: {
      type: Number,
      default: 0
    },
    subtitle: {
      type: String,
      default: null
    },
    valueType: {
      type: String,
      default: 'number', // 'number', 'currency', 'percentage'
      validator: value => ['number', 'currency', 'percentage'].includes(value)
    },
    dense: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    widgetClass() {
      return `widget-${this.color}`;
    },
    iconColor() {
      const colorMap = {
        primary: '#1976D2',
        success: '#4CAF50',
        warning: '#FF9800',
        error: '#F44336',
        info: '#2196F3',
        secondary: '#757575'
      };
      return colorMap[this.color] || '#1976D2';
    },
    formattedValue() {
      if (this.valueType === 'currency') {
        return `₹${this.formatNumber(this.value)}`;
      } else if (this.valueType === 'percentage') {
        return `${this.value}%`;
      }
      return this.formatNumber(this.value);
    },
    trendIcon() {
      return this.trendDirection === 'up' ? 'mdi-trending-up' : 
             this.trendDirection === 'down' ? 'mdi-trending-down' : 'mdi-minus';
    },
    trendColor() {
      return this.trendDirection === 'up' ? 'success' : 
             this.trendDirection === 'down' ? 'error' : 'grey';
    },
    trendClass() {
      return `trend-${this.trendDirection}`;
    },
    progressColor() {
      return this.color;
    },
    showFooter() {
      return this.showProgress || this.subtitle;
    }
  },
  methods: {
    formatNumber(num) {
      if (typeof num !== 'number') return num;
      
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toLocaleString();
    },
    refreshWidget() {
      this.$emit('refresh');
    },
    handleMenuAction(action) {
      this.$emit('menu-action', action);
    }
  }
};
</script>

<style scoped>
.dashboard-widget {
  border-radius: 16px !important;
  transition: all 0.3s ease;
  border: none;
  overflow: hidden;
  height: 100%;
}

.dashboard-widget:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.widget-icon {
  background: rgba(25, 118, 210, 0.1);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-primary .widget-icon { background: rgba(25, 118, 210, 0.1); }
.widget-success .widget-icon { background: rgba(76, 175, 80, 0.1); }
.widget-warning .widget-icon { background: rgba(255, 152, 0, 0.1); }
.widget-error .widget-icon { background: rgba(244, 67, 54, 0.1); }
.widget-info .widget-icon { background: rgba(33, 150, 243, 0.1); }
.widget-secondary .widget-icon { background: rgba(117, 117, 117, 0.1); }

.widget-actions {
  display: flex;
  gap: 4px;
}

.widget-content {
  text-align: left;
}

.widget-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
  line-height: 1;
}

.widget-title {
  font-size: 1rem;
  color: #6c757d;
  margin-bottom: 12px;
  font-weight: 500;
}

.widget-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.trend-up { color: #4CAF50; }
.trend-down { color: #F44336; }
.trend-neutral { color: #757575; }

.trend-period {
  color: #9e9e9e;
  font-weight: 400;
  margin-left: 4px;
}

.widget-footer {
  margin-top: 16px;
}

.widget-subtitle {
  color: #6c757d;
  font-size: 0.875rem;
  margin-top: 8px;
}

/* Dense/compact variant */
.dashboard-widget--dense .v-card__text {
  padding: 12px !important;
}

.dashboard-widget--dense .widget-header {
  margin-bottom: 8px;
}

.dashboard-widget--dense .widget-icon {
  width: 32px;
  height: 32px;
}

.dashboard-widget--dense .widget-value {
  font-size: 1.5rem;
  margin-bottom: 2px;
}

.dashboard-widget--dense .widget-title {
  font-size: 0.75rem;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboard-widget--dense .widget-trend {
  font-size: 0.7rem;
  margin-top: 4px;
}

.dashboard-widget--dense .widget-footer {
  margin-top: 6px;
}

.dashboard-widget--dense .widget-subtitle {
  font-size: 0.7rem;
  margin-top: 2px;
}

/* Dark theme */
.theme--dark .widget-value {
  color: #ffffff;
}

.theme--dark .widget-title {
  color: #b0b0b0;
}

.theme--dark .widget-subtitle {
  color: #b0b0b0;
}

.theme--dark .trend-period {
  color: #757575;
}
</style>